import axios from 'axios';
import store from '@/store/';
import { Api, Url, Crypto, SessionCache, Platform } from '@/utils/';

export default class Conversations {
    static getList (index, folderId) {
        let totalLimit;
        let limit = 100;
        const paginatableIndices = ['index_public_unarchived', 'index_public_unread', 'index_archived'];

        if (typeof index === 'undefined') {
            index = 'index_public_unarchived';
        }

        if (index === 'folder') {
            index = index + '/' + folderId;
        }

        if (index === 'index_public_unarchived') {
            limit = 75;
            totalLimit = 75;
        }

        const constructedURL = Url.get('conversations') + index + Url.getAccountParam();

        const promise = new Promise((resolve, reject) => {
            if (!SessionCache.hasConversations(index)) {
                if (paginatableIndices.includes(index)) {
                    Api.fetchResourcesInChunks(constructedURL, limit, Crypto.decryptConversation, totalLimit)
                        .then(conversations => {
                            SessionCache.putConversations(conversations, index);
                            resolve(conversations);
                        })
                        .catch(reject);
                } else {
                    Api.fetchResources(constructedURL, Crypto.decryptConversation)
                        .then(conversations => {
                            SessionCache.putConversations(conversations, index);
                            resolve(conversations);
                        })
                        .catch(reject);
                }
            } else {
                resolve(SessionCache.getConversations(index));
            }
        });

        return promise;
    }

    static getById(id) {
        let constructed_url = Url.get('conversation') + id + Url.getAccountParam();
        const promise = new Promise((resolve, reject) => {
            axios.get(constructed_url)
                .then(response => {
                    response = Crypto.decryptConversation(response.data);
                    if (response != null) {
                        resolve(response);
                    }
                })
                .catch(response => Api.rejectHandler(response, reject));
        });

        return promise;
    }

    static update(conversation_id, params) {
        let constructed_url = Url.get('update_conversation') + conversation_id + '?' + Url.getXUserTokenParam();
        axios.post(constructed_url, params, { 'Content-Type': 'application/json' })
            .catch(response => console.log(response));
    }

    // Resolves once both requests finish; rejects only if marking it read failed.
    static read(conversation_id) {
        // Read conversation
        let constructed_url = Url.get('read') + conversation_id + Url.getAccountParam();
        const read = axios.post(constructed_url)
            .catch((e) => {
                Api.rejectHandler(e);
                throw e;
            });

        // Dismiss notifiction. The phone's read_conversation handler doesn't clear it, this does.
        constructed_url = Url.get('dismiss') + Url.getAccountParam()
            + "&id=" + conversation_id;
        const dismiss = axios.post(constructed_url)
            .catch(() => { });

        return Promise.all([read, dismiss]);
    }

    /**
     * PULS-1424: marks several conversations read, as Android's DataSource.readConversations does:
     * one conversations/read per conversation. Each request pushes to every device, so they go one
     * at a time instead of all at once. Resolves with the ids that failed. Rejects, without sending
     * the rest, when the session has expired: rejectHandler has already logged out on that 401, and
     * every later request would just fail and log out again.
     */
    static async readMany(conversation_ids) {
        const failed = [];
        for (const id of conversation_ids) {
            try {
                await Conversations.read(id);
            } catch (e) {
                if (e.response && e.response.status === 401) {
                    throw e;
                }

                failed.push(id);
            }
        }

        return failed;
    }

    /**
     * PULS-1424: every conversation, archived ones included, for picking a folder's conversations
     * (Android's DataSource.getAllConversationsAsList). Not cached: the cached lists are per index.
     */
    static getAll() {
        const constructed_url = Url.get('conversations') + Url.getAccountParam();
        return Api.fetchResourcesInChunks(constructed_url, 100, Crypto.decryptConversation)
            .then(conversations => {
                // The pages are offsets into a list ordered by pinned and timestamp, so a message
                // arriving mid-fetch can shift a conversation onto two pages. Keep the first copy.
                // fetchResourcesInChunks also keeps rows that failed to decrypt, still encrypted.
                const seen = new Set();
                return conversations.filter(conversation => {
                    const id = String(conversation.device_id);
                    if (typeof conversation.titleNoEmoji !== 'string' || seen.has(id)) {
                        return false;
                    }

                    seen.add(id);
                    return true;
                });
            });
    }

    // PULS-1424: as Android's ApiUtils.addConversationToFolder / removeConversationFromFolder. The
    // server pushes add_conversation_to_folder / remove_conversation_from_folder to the other devices.
    static addToFolder(conversation_id, folder_id) {
        const constructed_url = Url.get('add_to_folder') + conversation_id + Url.getAccountParam()
            + '&folder_id=' + folder_id;
        return axios.post(constructed_url)
            .then(() => Conversations.folderChanged(conversation_id, folder_id))
            .catch(e => {
                Api.rejectHandler(e);
                throw e;
            });
    }

    static removeFromFolder(conversation_id) {
        const constructed_url = Url.get('remove_from_folder') + conversation_id + Url.getAccountParam();
        return axios.post(constructed_url)
            .then(() => Conversations.folderChanged(conversation_id, -1))
            .catch(e => {
                Api.rejectHandler(e);
                throw e;
            });
    }

    // What the push handlers in stream.js do, applied now: the server's push back to this browser
    // can lag (and a local backend sends none). Applying it twice is harmless.
    static folderChanged(conversation_id, folder_id) {
        SessionCache.setConversationFolder([conversation_id], folder_id);
        store.state.msgbus.$emit('foldersChanged');
        store.state.msgbus.$emit('conversationFolderChanged', { ids: [String(conversation_id)], folderId: folder_id });
    }

    static archive(conversation_id, archive) {
        let constructed_url;

        if (archive) {
            constructed_url = Url.get('archive');
        } else {
            constructed_url = Url.get('unarchive');
        }

        constructed_url += conversation_id + Url.getAccountParam();
        axios.post(constructed_url);
    }

    static delete(conversation_id) {
        let constructed_url = Url.get('delete') + conversation_id + Url.getAccountParam();
        axios.post(constructed_url);
    }

    static createWithImage(to, messageId, mimeType) {
        const constructed_url = Url.get("new_thread");

        let request = {
            account_id: store.state.account_id,
            to: to,
            message: "firebase -1",
            mime_type: mimeType,
            message_id: messageId,
            sent_device: Platform.getPlatformIdentifier()
        };

        request = { ...request, ...Url.getXUserTokenPayload() };

        const promise = new Promise((resolve, reject) => {
            axios.post(constructed_url, request, { 'Content-Type': 'application/json' })
                .then(response => resolve(response))
                .catch(response => Api.rejectHandler(response, reject));
        });

        return promise;
    }

    static create(to, message) {
        const constructed_url = Url.get("new_thread");

        let request = {
            account_id: store.state.account_id,
            to: to,
            message: message,
            sent_device: Platform.getPlatformIdentifier()
        };

        request = { ...request, ...Url.getXUserTokenPayload() };

        const promise = new Promise((resolve, reject) => {
            axios.post(constructed_url, request, { 'Content-Type': 'application/json' })
                .then(response => resolve(response))
                .catch(response => Api.rejectHandler(response, reject));
        });

        return promise;
    }
}
